import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
import {
    Button,
    Input,
    Label,
    ListBox,
    Select,
    Switch,
    TextArea,
    TextField,
} from '@heroui/react';
import { Sheet } from '@heroui-pro/react';
import type {
    Shareholder,
    ShareholderResidency,
    ShareholderType,
} from '@/api/models';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import { RESIDENCY_KEY, SHAREHOLDER_TYPE_KEY } from '../../dividendsMeta';
import {
    useShareholderForm,
    type ShareholderFormModel,
} from './useShareholderForm';

type ShareholderFormDrawerProps = {
    isOpen: boolean;
    shareholder: Shareholder | null;
    asOf: string;
    onClose: () => void;
    /** Fired once, after a real save success — never on cancel. */
    onSaved?: () => void;
};

const TYPES: ShareholderType[] = ['INDIVIDUAL', 'COMPANY'];
const RESIDENCIES: ShareholderResidency[] = ['RESIDENT', 'NON_RESIDENT'];

/** Field-level error text, shown only once the user has tried to submit. */
const useErrorText = (form: ShareholderFormModel) => {
    const { t } = useTranslation('pages');
    return (field: string): string | null => {
        const key = form.errors[field];
        return key && form.submitted ? t(key) : null;
    };
};

/**
 * Right-side drawer hosting the add / edit shareholder form — sibling of the
 * Chart of Accounts CRUD drawer and the employee drawer (same HeroUI Pro `Sheet`
 * chrome, vocabulary and focus handling). The Sheet owns focus trap and restore;
 * the hook owns all behaviour.
 *
 * Two controls carry more weight than their size suggests, and both say so:
 *
 * - **Withholding rate** seeds from the entity default and is badged when it
 *   differs, because a treaty rate entered here is what will actually be withheld
 *   from that person.
 * - **CIT-exempt** removes corporate income tax from this holder's slice
 *   entirely. It is the Georgian-legal-entity case, and getting it wrong changes
 *   what the company owes, not just what the shareholder receives.
 */
export const ShareholderFormDrawer: React.FC<ShareholderFormDrawerProps> = ({
    isOpen,
    shareholder,
    asOf,
    onClose,
    onSaved,
}) => {
    const { t } = useTranslation('pages');
    const form = useShareholderForm({ shareholder, asOf, onClose, onSaved });
    const errorText = useErrorText(form);
    const isEdit = shareholder !== null;

    return (
        <Sheet
            isOpen={isOpen}
            onOpenChange={(open) => !open && onClose()}
            placement="right"
        >
            <Sheet.Backdrop>
                <Sheet.Content className="w-full sm:max-w-[560px]">
                    <Sheet.Dialog
                        aria-label={t(
                            isEdit
                                ? 'dividends.form.editTitle'
                                : 'dividends.form.createTitle',
                        )}
                        {...getNodeTestIdProp('dividends-shareholder-drawer')}
                    >
                        <Sheet.CloseTrigger />
                        <Sheet.Header>
                            <Sheet.Heading>
                                <span className="flex flex-col gap-1">
                                    <span className="text-lg font-semibold">
                                        {t(
                                            isEdit
                                                ? 'dividends.form.editTitle'
                                                : 'dividends.form.createTitle',
                                        )}
                                    </span>
                                    <span className="text-sm font-normal text-muted">
                                        {t('dividends.form.subtitle')}
                                    </span>
                                </span>
                            </Sheet.Heading>
                        </Sheet.Header>

                        <Sheet.Body>
                            <div
                                className="flex flex-col gap-5"
                                {...getNodeTestIdProp(
                                    'dividends-shareholder-form',
                                )}
                            >
                                {/* Bilingual name */}
                                <div className="flex flex-col gap-1">
                                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                        <TextField
                                            className="flex flex-col gap-1"
                                            onChange={form.onNameKaChange}
                                            value={form.nameKa}
                                            {...getNodeTestIdProp(
                                                'dividends-form-name-ka',
                                            )}
                                        >
                                            <Label>
                                                {t(
                                                    'dividends.form.fields.nameKa',
                                                )}
                                            </Label>
                                            <Input />
                                        </TextField>
                                        <TextField
                                            className="flex flex-col gap-1"
                                            onChange={form.onNameEnChange}
                                            value={form.nameEn}
                                            {...getNodeTestIdProp(
                                                'dividends-form-name-en',
                                            )}
                                        >
                                            <Label>
                                                {t(
                                                    'dividends.form.fields.nameEn',
                                                )}
                                            </Label>
                                            <Input />
                                        </TextField>
                                    </div>
                                    {errorText('name') ? (
                                        <span className="text-xs text-danger">
                                            {errorText('name')}
                                        </span>
                                    ) : null}
                                </div>

                                {/* Identifier */}
                                <TextField
                                    className="flex w-full max-w-[16rem] flex-col gap-1"
                                    isInvalid={errorText('identifier') !== null}
                                    onChange={form.onIdentifierChange}
                                    value={form.identifier}
                                    {...getNodeTestIdProp(
                                        'dividends-form-identifier',
                                    )}
                                >
                                    <Label>
                                        {t('dividends.form.fields.identifier')}
                                    </Label>
                                    <Input
                                        className="tabular-nums"
                                        inputMode="numeric"
                                    />
                                    <span className="text-xs text-muted">
                                        {t(
                                            'dividends.form.fields.identifierHint',
                                        )}
                                    </span>
                                    {errorText('identifier') ? (
                                        <span className="text-xs text-danger">
                                            {errorText('identifier')}
                                        </span>
                                    ) : null}
                                </TextField>

                                {/* Type + residency */}
                                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                    <Select
                                        onChange={(next) =>
                                            next &&
                                            form.onTypeChange(
                                                String(next) as ShareholderType,
                                            )
                                        }
                                        value={form.type}
                                    >
                                        <Label>
                                            {t('dividends.form.fields.type')}
                                        </Label>
                                        <Select.Trigger
                                            {...getNodeTestIdProp(
                                                'dividends-form-type',
                                            )}
                                        >
                                            <Select.Value />
                                            <Select.Indicator />
                                        </Select.Trigger>
                                        <Select.Popover>
                                            <ListBox>
                                                {TYPES.map((option) => (
                                                    <ListBox.Item
                                                        id={option}
                                                        key={option}
                                                        textValue={t(
                                                            SHAREHOLDER_TYPE_KEY[
                                                                option
                                                            ],
                                                        )}
                                                    >
                                                        {t(
                                                            SHAREHOLDER_TYPE_KEY[
                                                                option
                                                            ],
                                                        )}
                                                        <ListBox.ItemIndicator />
                                                    </ListBox.Item>
                                                ))}
                                            </ListBox>
                                        </Select.Popover>
                                    </Select>

                                    <Select
                                        onChange={(next) =>
                                            next &&
                                            form.onResidencyChange(
                                                String(
                                                    next,
                                                ) as ShareholderResidency,
                                            )
                                        }
                                        value={form.residency}
                                    >
                                        <Label>
                                            {t(
                                                'dividends.form.fields.residency',
                                            )}
                                        </Label>
                                        <Select.Trigger
                                            {...getNodeTestIdProp(
                                                'dividends-form-residency',
                                            )}
                                        >
                                            <Select.Value />
                                            <Select.Indicator />
                                        </Select.Trigger>
                                        <Select.Popover>
                                            <ListBox>
                                                {RESIDENCIES.map((option) => (
                                                    <ListBox.Item
                                                        id={option}
                                                        key={option}
                                                        textValue={t(
                                                            RESIDENCY_KEY[
                                                                option
                                                            ],
                                                        )}
                                                    >
                                                        {t(
                                                            RESIDENCY_KEY[
                                                                option
                                                            ],
                                                        )}
                                                        <ListBox.ItemIndicator />
                                                    </ListBox.Item>
                                                ))}
                                            </ListBox>
                                        </Select.Popover>
                                    </Select>
                                </div>

                                {/* Holding + withholding */}
                                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                    <TextField
                                        className="flex flex-col gap-1"
                                        isInvalid={
                                            errorText('holding') !== null
                                        }
                                        onChange={form.onHoldingChange}
                                        value={form.holding}
                                        {...getNodeTestIdProp(
                                            'dividends-form-holding',
                                        )}
                                    >
                                        <Label>
                                            {t('dividends.form.fields.holding')}
                                        </Label>
                                        <Input
                                            className="text-right tabular-nums"
                                            inputMode="decimal"
                                        />
                                        {errorText('holding') ? (
                                            <span className="text-xs text-danger">
                                                {errorText('holding')}
                                            </span>
                                        ) : null}
                                    </TextField>

                                    <TextField
                                        className="flex flex-col gap-1"
                                        isInvalid={
                                            errorText('withholdingTax') !== null
                                        }
                                        onChange={form.onWithholdingChange}
                                        value={form.withholdingTax}
                                        {...getNodeTestIdProp(
                                            'dividends-form-withholding',
                                        )}
                                    >
                                        <span className="flex items-center justify-between gap-2">
                                            <Label>
                                                {t(
                                                    'dividends.form.fields.withholding',
                                                )}
                                            </Label>
                                            {form.isRateOverridden ? (
                                                <span
                                                    className="inline-flex w-fit items-center rounded-lg bg-warning-soft px-2 py-0.5 text-xs font-medium text-warning-soft-foreground"
                                                    {...getNodeTestIdProp(
                                                        'dividends-form-rate-override',
                                                    )}
                                                >
                                                    {t(
                                                        'dividends.rateOverridden',
                                                    )}
                                                </span>
                                            ) : null}
                                        </span>
                                        <Input
                                            className="text-right tabular-nums"
                                            inputMode="decimal"
                                        />
                                        <span className="text-xs text-muted">
                                            {t(
                                                'dividends.form.fields.withholdingHint',
                                                {
                                                    rate: form.defaultWithholdingText,
                                                },
                                            )}
                                        </span>
                                        {errorText('withholdingTax') ? (
                                            <span className="text-xs text-danger">
                                                {errorText('withholdingTax')}
                                            </span>
                                        ) : null}
                                    </TextField>
                                </div>

                                <TextField
                                    className="flex flex-col gap-1"
                                    onChange={form.onWithholdingNoteChange}
                                    value={form.withholdingNote}
                                    {...getNodeTestIdProp(
                                        'dividends-form-withholding-note',
                                    )}
                                >
                                    <Label className="text-xs text-muted">
                                        {t(
                                            'dividends.form.fields.withholdingNote',
                                        )}
                                    </Label>
                                    <TextArea
                                        placeholder={t(
                                            'dividends.form.fields.withholdingNotePlaceholder',
                                        )}
                                        rows={2}
                                        variant="secondary"
                                    />
                                </TextField>

                                {/* CIT exemption */}
                                <div className="flex items-start justify-between gap-4 rounded-xl bg-surface-secondary p-3">
                                    <div className="flex min-w-0 flex-col gap-0.5">
                                        <span className="text-sm font-medium text-foreground">
                                            {t(
                                                'dividends.form.fields.citExempt',
                                            )}
                                        </span>
                                        <span className="text-xs text-muted">
                                            {t(
                                                'dividends.form.fields.citExemptHint',
                                            )}
                                        </span>
                                    </div>
                                    <Switch
                                        aria-label={t(
                                            'dividends.form.fields.citExempt',
                                        )}
                                        isSelected={form.citExempt}
                                        onChange={form.onCitExemptChange}
                                        {...getNodeTestIdProp(
                                            'dividends-form-cit-exempt',
                                        )}
                                    >
                                        <Switch.Content>
                                            <Switch.Control>
                                                <Switch.Thumb />
                                            </Switch.Control>
                                        </Switch.Content>
                                    </Switch>
                                </div>

                                {form.saveError ? (
                                    <p
                                        className="text-sm text-danger"
                                        {...getNodeTestIdProp(
                                            'dividends-form-error',
                                        )}
                                    >
                                        {form.saveError}
                                    </p>
                                ) : null}

                                <div className="flex items-center justify-end gap-2">
                                    <Button
                                        onPress={onClose}
                                        variant="tertiary"
                                        {...getNodeTestIdProp(
                                            'dividends-form-cancel',
                                        )}
                                    >
                                        {t('dividends.actions.cancel')}
                                    </Button>
                                    <Button
                                        isPending={form.isSaving}
                                        onPress={form.onSubmit}
                                        variant="primary"
                                        {...getNodeTestIdProp(
                                            'dividends-form-save',
                                        )}
                                    >
                                        <Icon
                                            aria-hidden
                                            icon="gravity-ui:check"
                                        />
                                        {form.isEdit
                                            ? t('dividends.actions.saveChanges')
                                            : t(
                                                  'dividends.actions.addShareholder',
                                              )}
                                    </Button>
                                </div>

                                {/* Removal, on an edit only — below the save
                                    actions and behind a rule, so the
                                    destructive control is nowhere near the one
                                    the user came here to press. */}
                                {form.isEdit ? (
                                    <div className="flex flex-col gap-2 border-t border-separator pt-4">
                                        {form.isDeleteArmed ? (
                                            <div className="flex flex-wrap items-center gap-3">
                                                <span
                                                    className="text-sm text-foreground"
                                                    {...getNodeTestIdProp(
                                                        'dividends-form-delete-confirm',
                                                    )}
                                                >
                                                    {t(
                                                        'dividends.form.delete.confirm',
                                                    )}
                                                </span>
                                                <Button
                                                    isPending={form.isDeleting}
                                                    onPress={form.onDelete}
                                                    size="sm"
                                                    variant="danger"
                                                    {...getNodeTestIdProp(
                                                        'dividends-form-delete-do',
                                                    )}
                                                >
                                                    {t(
                                                        'dividends.actions.deleteShareholder',
                                                    )}
                                                </Button>
                                                <Button
                                                    onPress={
                                                        form.onDeleteCancel
                                                    }
                                                    size="sm"
                                                    variant="tertiary"
                                                    {...getNodeTestIdProp(
                                                        'dividends-form-delete-cancel',
                                                    )}
                                                >
                                                    {t(
                                                        'dividends.actions.keepShareholder',
                                                    )}
                                                </Button>
                                            </div>
                                        ) : (
                                            <Button
                                                className="w-fit"
                                                onPress={form.onDeleteArm}
                                                variant="danger-soft"
                                                {...getNodeTestIdProp(
                                                    'dividends-form-delete',
                                                )}
                                            >
                                                <Icon
                                                    aria-hidden
                                                    icon="gravity-ui:trash-bin"
                                                />
                                                {t(
                                                    'dividends.actions.deleteShareholder',
                                                )}
                                            </Button>
                                        )}

                                        {/* Stated up front rather than only on
                                            the refusal: someone reaching for
                                            Delete when they mean "they no longer
                                            hold shares" should read this before
                                            pressing anything. */}
                                        <span
                                            className="text-xs text-muted"
                                            {...getNodeTestIdProp(
                                                'dividends-form-delete-note',
                                            )}
                                        >
                                            {t('dividends.form.delete.note')}
                                        </span>

                                        {form.deleteError ? (
                                            <span
                                                className="text-xs text-danger"
                                                {...getNodeTestIdProp(
                                                    'dividends-form-delete-error',
                                                )}
                                            >
                                                {form.deleteError}
                                            </span>
                                        ) : null}
                                    </div>
                                ) : null}
                            </div>
                        </Sheet.Body>
                    </Sheet.Dialog>
                </Sheet.Content>
            </Sheet.Backdrop>
        </Sheet>
    );
};
